import React, { useState, useCallback } from 'react';
import { Upload, ArrowRight, ArrowLeft, FileText } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card.tsx';
import { jsPDF } from 'jspdf';

const ResumeComparisonForm = ({ isRecruiter }) => {
    const [uploadedFiles, setUploadedFiles] = useState<any>({});
    const [currentStep, setCurrentStep] = useState(1);
    const [message, setMessage] = useState('');
    const [isUploading, setIsUploading] = useState(false);
    const [progressStage, setProgressStage] = useState(0);

    const convertTextToPDF = (text: string, fileName: string) => {
        const doc = new jsPDF();
        doc.text(text, 10, 10);
        return doc.output('blob');
    };

    const handleCompare = useCallback(async (Promise: any) => {
        const filePath = Promise.resumePath;
        const JD = Promise.jobDescriptionPath;
        const actualResumeIdValue = Promise.resumeId;
        const jdID = Promise.jobDescriptionID;
        const resumeIdValue = Promise.resumeId;

        const response = await fetch('/compare', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                resumeId: resumeIdValue,
                jobDescriptionID: jdID,
                resumePath: filePath,
                jobDescriptionPath: JD,
                actualResumeId: actualResumeIdValue
            })
        });

        if (!response.ok) {
            throw new Error(`Comparison failed: ${response.statusText}`);
        }

        const data = await response.json();
        // setMessage(data.message);

        const report = document.getElementById('Report');
        const content = document.getElementById('content');

        if (report && content) {
            report.innerHTML = data.message;
            content.style.display = 'none';
            report.style.display = 'block';
            report.style.textWrap = "auto";
        }
    }, []);

    const handleUpload = async () => {
        // if (!isRecruiter.isLoggedIn) {
        //     alert("Please log in or sign up to upload a resume.");
        //     // Optionally, redirect to login or sign-up page
        //     window.location.href = '/login'; // or '/signup'
        //     return;
        // }

        const formData = new FormData();

        // the resume must be search able to get this far. 
        formData.append("Searchable", "true");
        // this means that recruiters can find them 
        if (uploadedFiles.resume) {
            formData.append('resume', uploadedFiles.resume);
        }
        if (uploadedFiles.jobDescription) {
            formData.append('jobDescription', uploadedFiles.jobDescription);
        }

        const jobDescriptionText = document.getElementById('jobDescriptionText') as HTMLTextAreaElement;
        if (jobDescriptionText?.value) {
            const jobDescriptionBlob = convertTextToPDF(jobDescriptionText.value, 'jobDescription.pdf');
            formData.append('jobDescription', jobDescriptionBlob, 'jobDescription.pdf');
        }

        const resumeText = document.getElementById('resumeText') as HTMLTextAreaElement;
        if (resumeText?.value) {
            const resumeBlob = convertTextToPDF(resumeText.value, 'resume.pdf');
            formData.append('resume', resumeBlob, 'resume.pdf');
        }

        try {
            const response = await fetch('/upload', {
                method: 'POST',
                body: formData
            });

            if (response.ok) {
                const data = await response.json();
                console.log(data)
                setUploadedFiles({
                    resume: data.resumeId,
                    jobDescription: data.jobDescriptionId,
                    resumePath: data.resumePath,
                    jobDescriptionPath: data.jobDescriptionPath
                });
                await handleCompare(data);
                return Promise.resolve();
            }
            throw new Error('Upload failed');
        } catch (error) {
            setMessage('Error uploading files.');
            console.error('Error:', error);
            return Promise.reject(error);
        }
    };

    const nextStep = async () => {
        if (currentStep === 2) {
            handleUpload();
            setCurrentStep(prevStep => prevStep + 1);
            setIsUploading(true);
            setProgressStage(1);
            await new Promise(resolve => setTimeout(resolve, 6000));
            setProgressStage(2);
            await new Promise(resolve => setTimeout(resolve, 30000));
            setProgressStage(3);
            await new Promise(resolve => setTimeout(resolve, 20000));

        }
        setCurrentStep(prevStep => prevStep + 1);
    };

    const previousStep = () => {
        setCurrentStep(prevStep => prevStep - 1);
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, files } = e.target;
        if (files && files.length > 0) {
            setUploadedFiles(prevState => ({
                ...prevState,
                [name]: files[0]
            }));
        }
    };

    const renderProgressIndicator = () => {
        if (progressStage === 0) return null;

        const stages = {
            1: 'Uploading Files...',
            2: 'Comparing Documents...',
            3: 'Generating Report...'
        };

        return (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <div className="flex items-center space-x-3">
                    <svg className="animate-spin h-5 w-5 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    <span className="text-blue-700">{stages[progressStage as keyof typeof stages]}</span>
                </div>
                <div className="mt-3 w-full bg-blue-100 rounded-full h-2">
                    <div
                        className="bg-blue-500 h-2 rounded-full transition-all duration-500"
                        style={{ width: `${(progressStage / 3) * 100}%` }}
                    />
                </div>
            </div>
        );
    };

    const FileUploadSection = ({ id, label, placeholder }) => {
        const [fileName, setFileName] = useState('');

        const handleFileChange = (event) => {
            const file = event.target.files[0];
            if (file) {
                setFileName(file.name);
                setUploadedFiles(prevState => ({
                    ...prevState,
                    [id]: file
                }));
            } else {
                setFileName('');
                setUploadedFiles(prevState => ({
                    ...prevState,
                    [id]: null
                }));
            }
        };

        return (
            <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-blue-500 transition-colors">
                    {!fileName ? (
                        <>
                            <input
                                type="file"
                                id={id}
                                name={id}
                                accept=".pdf"
                                onChange={handleFileChange}
                                className="hidden"
                            />
                            <label htmlFor={id} className="cursor-pointer">
                                <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                                <p className="text-sm font-medium text-gray-700">{label}</p>
                                <p className="text-xs text-gray-500 mt-1">PDF, DOCX, or TXT files</p>
                            </label>
                        </>
                    ) : (
                        <p className="text-sm font-medium text-gray-700">Attached file: {fileName}</p>
                    )}
                </div>
                <div className="relative">
                    <div className="absolute inset-0 h-px flex items-center">
                        <div className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center">
                        <span className="px-2 bg-white text-sm text-gray-500">or</span>
                    </div>
                </div>
                <textarea
                    id={`${id}Text`}
                    rows={6}
                    placeholder={placeholder}
                    className="w-full p-3 border border-gray-300 rounded-lg text-sm resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
            <pre id="Report" className="bg-white shadow-md rounded-lg p-6 w-full max-w-4xl mx-auto hidden" />
            <div id="content" className="max-w-3xl mx-auto">
                <Card className="max-w-3xl mx-auto">
                    <CardHeader>
                        <CardTitle className="text-center">
                            Resume Comparison Tool
                        </CardTitle>
                        <p className="text-center text-gray-600 mt-2">
                            Compare your resume with a job description to see how well they match
                        </p>
                    </CardHeader>
                    <CardContent>
                        <div className="mb-8">
                            <div className="flex justify-between mb-2">
                                {['Upload Resume', 'Add Job Description', 'Compare'].map((step, index) => (
                                    <div key={step} className="flex flex-col items-center w-1/3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${currentStep > index + 1 ? 'bg-green-500' :
                                            currentStep === index + 1 ? 'bg-blue-500' : 'bg-gray-200'
                                            } text-white`}>
                                            {currentStep > index + 1 ? '✓' : index + 1}
                                        </div>
                                        <span className={`text-sm ${currentStep === index + 1 ? 'text-blue-500 font-medium' : 'text-gray-500'}`}>
                                            {step}
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="relative w-full h-2 bg-gray-200 rounded-full">
                                <div
                                    className="absolute h-full bg-blue-500 rounded-full transition-all duration-300"
                                    style={{ width: `${((currentStep - 1) / 2) * 100}%` }}
                                />
                            </div>
                        </div>

                        {renderProgressIndicator()}

                        <div className="mt-8">
                            {currentStep === 1 && (
                                <><FileUploadSection
                                    id="resume"
                                    label="Upload your resume"
                                    placeholder="Or paste your resume content here..." />
                                    <div className="mt-4">
                                        <label className="flex items-center">
                                            <input type="checkbox" className="form-checkbox" required />
                                            <span className="ml-2 text-sm text-gray-600">
                                                I agree to the <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">Terms and Conditions</a> and <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-500 underline">Privacy Policy</a>
                                            </span>
                                        </label>
                                    </div>
                                </>
                            )}

                            {currentStep === 2 && (
                                <FileUploadSection
                                    id="jobDescription"
                                    label="Upload job description"
                                    placeholder="Or paste the job description here..."
                                />
                            )}

                            {currentStep === 3 && (
                                <div className="text-center py-8">
                                    <FileText className="w-16 h-16 mx-auto mb-4 text-blue-500 animate-pulse" />
                                    <p className="text-lg font-medium">Analyzing your resume...</p>
                                    <p className="text-sm text-gray-500 mt-2">This may take a few moments</p>
                                </div>
                            )}

                            {currentStep !== 3 && !isUploading && (
                                <div className="flex justify-between mt-8">
                                    {currentStep > 1 && (
                                        <button
                                            onClick={previousStep}
                                            className="flex items-center px-4 py-2 text-gray-600 hover:text-gray-800"
                                            disabled={isUploading}
                                        >
                                            <ArrowLeft className="w-4 h-4 mr-2" />
                                            Back
                                        </button>
                                    )}
                                    <button
                                        onClick={nextStep}
                                        className="ml-auto flex items-center px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                                        disabled={isUploading}
                                    >
                                        {currentStep === 2 ? 'Compare' : 'Next'}
                                        <ArrowRight className="w-4 h-4 ml-2" />
                                    </button>
                                </div>
                            )}
                        </div>

                        {message && (
                            <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg">
                                {message}
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
};

export default ResumeComparisonForm;